import React, { useEffect, useRef } from "react";

// Widget oficial de assinatura incorporada (embedded) do Clicksign para
// envelopes (API v2/v3). O script expõe `window.Clicksign`, que monta uma
// iframe apontando para:
//   {endpoint}/notarial/widget/signatures/{signerKey}/redirect?embedded=true
const SCRIPT_SRC =
  "https://cdn-public-library.clicksign.com/embedded/embedded.min-2.0.0.js";
const SCRIPT_ID = "clicksign-embedded-sdk";

function loadClicksignSdk() {
  return new Promise((resolve, reject) => {
    if (window.Clicksign) {
      resolve(window.Clicksign);
      return;
    }

    let script = document.getElementById(SCRIPT_ID);
    const onLoad = () => resolve(window.Clicksign);
    const onError = () =>
      reject(new Error("Não foi possível carregar o Clicksign."));

    if (script) {
      script.addEventListener("load", onLoad);
      script.addEventListener("error", onError);
      return;
    }

    script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);
    document.body.appendChild(script);
  });
}

// Cada instância monta a iframe num container próprio; garantimos ids únicos.
let widgetSeq = 0;

export default function ClicksignEmbedded({
  signerKey,
  endpoint,
  onLoaded,
  onSigned,
  onError,
  className,
}) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const containerId = useRef(`clicksign-widget-${(widgetSeq += 1)}`).current;

  useEffect(() => {
    if (!signerKey) return undefined;

    let cancelled = false;

    loadClicksignSdk()
      .then((Clicksign) => {
        if (cancelled || !Clicksign || !containerRef.current) return;

        const widget = new Clicksign(signerKey);
        widget.endpoint = endpoint || "https://app.clicksign.com";
        widget.origin = window.location.origin;

        widget.on("loaded", (data) => onLoaded && onLoaded(data));
        widget.on("signed", (data) => onSigned && onSigned(data));
        widget.on("resized", (data) => {
          if (containerRef.current && data && data.height) {
            containerRef.current.style.height = `${data.height}px`;
          }
        });

        widget.mount(containerId);
        widgetRef.current = widget;
      })
      .catch((error) => {
        if (!cancelled) onError && onError(error);
      });

    return () => {
      cancelled = true;
      try {
        if (widgetRef.current) widgetRef.current.unmount();
      } catch (error) {
        // ignora falhas ao desmontar (iframe já removida, etc.)
      }
      widgetRef.current = null;
    };
    // Remonta apenas quando a chave/endpoint mudarem.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signerKey, endpoint]);

  return <div id={containerId} ref={containerRef} className={className} />;
}
